import { Flex, Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import ListButton from 'components/listButton/listButton';
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import NoData from 'components/noData/noData';
import StatusComponent from 'components/status/status';

// services

// styles

// models
import NotificationService from 'core/services/notification';
import TableTotal from 'components/tableTotal/tableTotal';
import { StatusColors } from 'utils/statusColors';
import { ServiceCost, VerificationPayload } from '../../commonModels';
import { onGetServiceCost } from '../../commonServices';
import { Dividend, DividendListItem } from '../models/dividends';
import { dateFormat } from 'utils/dateFormat';
import { onCancelDividend, onGetDividendsList } from '../services/dividends';
import VerificationModal from '../../verificationModal';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

interface DividendsListPageProps {}

const DividendsListPage: FunctionComponent<DividendsListPageProps> = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<DividendListItem[]>([]);

	const [isCancelling, setIsCancelling] = useState<boolean>(false);
	const [selectedRequest, setSelectedRequest] = useState<DividendListItem>();
	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<DividendListItem>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'formated_request_number',
			key: 'formated_request_number',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status_key',
			key: 'status_key',
			render: (status_key) => <StatusComponent text={t(`COMMON.${status_key.toUpperCase()}`)} color={StatusColors[status_key]} />,
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'request_date',
			key: 'request_date',
			render: (request_date) => <>{dateFormat(request_date, true)}</>,
		},
		{
			title: t('ACTIONS.ELIGIBILITY_DATE'),
			dataIndex: 'eligibility_date',
			key: 'eligibility_date',
			render: (eligibility_date) => <>{dateFormat(eligibility_date, true)}</>,
		},
		{
			title: t('ACTIONS.DISTRIBUTION_DATE'),
			dataIndex: 'distribution_date',
			key: 'distribution_date',
			render: (distribution_date) => <>{dateFormat(distribution_date, true)}</>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => (
				<Flex gap={10} justify="center">
					<ListButton type="view" onClick={() => navigate(`view/${record.id}`)} />

					{record?.status_key === 'PendingPayment' && (
						<ListButton
							type="edit"
							{...suspendedProps}
							text={t('COMMON.EDIT')}
							onClick={() => {
								navigate(`${record.id}`);
							}}
						/>
					)}

					{['PendingPayment', 'Scheduled'].includes(record?.status_key) && (
						<ListButton
							type="reject"
							{...suspendedProps}
							text={t('COMMON.CANCEL')}
							onClick={() => {
								setSelectedRequest(record);
								setVerificationModalOpen(true);
							}}
						/>
					)}
				</Flex>
			),
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getDividendsList();
		getServiceCost();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const response = await onGetServiceCost('dividends');
			setServiceCost(response?.success);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getDividendsList = async () => {
		try {
			setIsLoading(true);
			const response = await onGetDividendsList(getCurrentPage());
			setRequests(response.dividends_actions);
			setTotalRecords(response.pagination.total);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });
			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetDividendsList(getCurrentPage());
			setRequests(response.dividends_actions);
			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const cancelHandler = async (payload: VerificationPayload) => {
		try {
			setIsCancelling(true);

			await onCancelDividend(selectedRequest?.id!, payload.key);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.DIVIDEND_CANCELLED_SUCCESS'));

			setVerificationModalOpen(false);
			setIsCancelling(false);
			getDividendsList();
			setSelectedRequest(undefined);
		} catch (error) {
			setIsCancelling(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.DIVIDEND_NOT_CANCELLED'));
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.DIVIDENDS')}
				subTitle={t('ACTIONS.DIVIDENDS_DESC')}
				costLoading={serviceCostLoading}
				costs={[serviceCost!]}
			/>

			<Table
				columns={columns}
				dataSource={requests}
				scroll={{ x: 600 }}
				rowKey="id"
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				loading={isLoading}
				locale={{ emptyText: <NoData /> }}
			/>

			<VerificationModal
				title={
					selectedRequest?.status_key === 'PendingPayment'
						? t('ACTIONS.CONFIRM_CANCEL_PENDING_DIVIDEND')
						: t('ACTIONS.CONFIRM_CANCEL_SCHEDULED_DIVIDEND')
				}
				subTitle={t('ACTIONS.PLEASE_CONFIRM_YOUR_REQUEST')}
				otpOnly
				open={verificationModalOpen}
				isLoading={isCancelling}
				className="confirmationModal"
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={cancelHandler}
				module="dividends_cancel"
				note={t('ACTIONS.DIVIDEND_CANCEL_NOTE')}
				noteType="red"
			/>
		</>
	);
};

export default DividendsListPage;
